/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import React, { useEffect, useState } from 'react';
import { Alert, ButtonPrimary, ButtonSecondary, IconDown, IconUp, Input, Label } from '@americanexpress/dls-react';

// services and models
import LiteralsService from '../../services/literals/LiteralsService';
import { CryptoModel } from 'src/types/crypto.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import InvestmentHoldingService from '../../services/investment-holding.service';
import TimeAgoService from '../../services/time-ago.service';
import FormatService from '../../services/format.service';

// components
import Drawer from '../../components/drawer/Drawer';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import styles from './CryptoDrawer.module.scss';
import { CryptoDrawerViewModel } from 'src/types/view-models/crypto-drawer.view.model';

interface DataEntryErrors {
    valueError: boolean;
    quantityError: boolean;
}

function nameSymbolString(asset: CryptoModel | InvestmentHoldingModel): string {
    return `${asset?.name} (${asset?.symbol})`;
}

export function getCryptoFilter(inputValue: string): (crypto: CryptoModel) => boolean {
    return function cryptoFilter(crypto: CryptoModel): boolean {
        return !inputValue || nameSymbolString(crypto)?.toLowerCase().includes(inputValue.toLowerCase());
    };
}

function validateInput(
    nameValue: string,
    selectedAsset: CryptoModel | InvestmentHoldingModel,
    quantity: number
): DataEntryErrors {
    return {
        valueError: !nameValue || nameValue === '' || !selectedAsset,
        quantityError: !quantity || quantity <= 0
    };
}

export function formatCryptoAmount(amount: number): string {
    if (amount >= 1.0) {
        return FormatService.formatCurrency(amount);
    }
    return `$${amount}`;
}

export default function CryptoDrawer({
    currentCrypto,
    cryptoList,
    handleBack,
    handleAccounts,
    handleReloadUser,
    history
}: CryptoDrawerViewModel): JSX.Element {
    const [valueList, setValueList] = useState<string[]>(cryptoList.map((x) => nameSymbolString(x)));
    const [value, setValue] = useState<string>(currentCrypto ? nameSymbolString(currentCrypto) : '');
    const [selectedAsset, setSelectedAsset] = useState<CryptoModel | InvestmentHoldingModel>(currentCrypto);
    const [errors, setErrors] = useState<DataEntryErrors>({ valueError: false, quantityError: false });
    const [quantity, setQuantity] = useState<number>(currentCrypto?.quantity || undefined);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [hasBlurred, setHasBlurred] = useState<boolean>(false);

    useEffect(() => {
        const newValueList = cryptoList.filter(getCryptoFilter(value)).map((x) => nameSymbolString(x));
        setValueList(newValueList);
        setErrors(validateInput(value, selectedAsset, quantity));
    }, [value]);

    useEffect(() => {
        setErrors(validateInput(value, selectedAsset, quantity));
    }, [quantity]);

    async function handleUpsert(e: any): Promise<void> {
        if (e.currentTarget) e.currentTarget.disabled = true;
        const asset: InvestmentHoldingModel = {
            ...currentCrypto,
            name: selectedAsset.name,
            symbol: selectedAsset.symbol,
            price: selectedAsset.price,
            quantity,
            amount: selectedAsset.price * quantity,
            type: 'crypto'
        };
        if (asset.id) {
            await InvestmentHoldingService.updateInvestmentHolding(asset);
        } else {
            await InvestmentHoldingService.postInvestmentHolding(asset);
        }
        await handleReloadUser();
        await handleAccounts();
    }

    async function handleDelete(e: any): Promise<void> {
        if (e.currentTarget) e.currentTarget.disabled = true;
        await InvestmentHoldingService.deleteInvestmentHolding(currentCrypto.id);
        await handleReloadUser();
        await handleAccounts();
    }

    const getMatches = (stringVal: string): CryptoModel[] => {
        return cryptoList.filter(getCryptoFilter(stringVal));
    };

    const handleNameChange = (e: any): void => {
        const newValue: string = e.target.value;
        if (newValue === '') {
            setValue('');
            setIsOpen(false);
        } else {
            const matches: CryptoModel[] = getMatches(newValue);
            if (matches.length > 0) {
                setValue(newValue);
                setIsOpen(true);
            }
        }
    };

    const handleNameBlur = (): void => {
        setHasBlurred(true);
        const matches: CryptoModel[] = getMatches(value);
        if (matches.length === 1) {
            const match: CryptoModel = matches[0];
            setValue(nameSymbolString(match));
            setSelectedAsset(match);
            setIsOpen(false);
        } else {
            setSelectedAsset(undefined);
        }
    };

    const handleQuantityChange = (e: any): void => {
        const newQuantity = e.target.value;
        if (newQuantity === '') setQuantity(undefined);
        else if (newQuantity >= 0) setQuantity(newQuantity);
    };

    const handleListItemClick = (e: any, item: string): void => {
        setValue(item);
        const matches: CryptoModel[] = getMatches(item);
        if (matches.length === 1) {
            const match: CryptoModel = matches[0];
            setSelectedAsset(match);
        }
        setIsOpen(false);
    };

    const totalAmount: number = (quantity || 0) * (selectedAsset?.price || 0);

    return (
        <Drawer
            title={
                currentCrypto
                    ? LiteralsService.getLiteral('CryptoDrawer.titles.update')
                    : LiteralsService.getLiteral('CryptoDrawer.titles.add')
            }
            withBackButton={true}
            handleBack={handleBack}
            drawerProps={{ history }}
        >
            <div className={styles.cryptoDrawer} data-cy="cryptoDrawer">
                <div>
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        {currentCrypto
                            ? LiteralsService.getLiteral('CryptoDrawer.headings.update')
                            : LiteralsService.getLiteral('CryptoDrawer.headings.add')}
                    </Typography>
                </div>

                <div>
                    <Label htmlFor="cryptoName">
                        {LiteralsService.getLiteral('CryptoDrawer.currencyNameField.label')}
                    </Label>
                    <div className={`${styles.nameInput} ${isOpen ? styles.unroundedBottomBorder : ''}`}>
                        <Input
                            placeholder={LiteralsService.getLiteral('CryptoDrawer.currencyNameField.placeholder')}
                            value={value || ''}
                            onChange={handleNameChange}
                            onBlur={handleNameBlur}
                            id="cryptoName"
                            data-cy="cryptoName"
                        />
                        <button
                            className={styles.toggleButton}
                            aria-label="toggle menu"
                            type="button"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            {isOpen ? <IconUp /> : <IconDown />}
                        </button>
                    </div>
                    {isOpen && (
                        <ul className={styles.ul} data-cy="cryptoList">
                            {valueList.map((item, index) => (
                                <li
                                    key={`${item}${index}`}
                                    value={item}
                                    onClick={(e: any) => handleListItemClick(e, item)}
                                >
                                    {item}
                                </li>
                            ))}
                        </ul>
                    )}
                    {hasBlurred && errors?.valueError && (
                        <Alert data-cy="cryptoValueError">
                            {LiteralsService.getLiteral('CryptoDrawer.currencyNameField.errorLabel')}{' '}
                            {LiteralsService.getLiteral('CryptoDrawer.isRequired')}.
                        </Alert>
                    )}
                </div>

                <div>
                    <Label htmlFor="cryptoQuantity">
                        {LiteralsService.getLiteral('CryptoDrawer.currencyQuantityField.label')}
                    </Label>
                    <Input
                        id="cryptoQuantity"
                        variant="outlined"
                        type="number"
                        data-cy="cryptoQuantity"
                        value={quantity || ''}
                        onChange={handleQuantityChange}
                        onBlur={() => setHasBlurred(true)}
                    />
                    {hasBlurred && errors?.quantityError && (
                        <Alert data-cy="cryptoQuantityError">
                            {LiteralsService.getLiteral('CryptoDrawer.currencyQuantityField.errorLabel')}{' '}
                            {LiteralsService.getLiteral('CryptoDrawer.isRequired')}.
                        </Alert>
                    )}
                </div>

                <div>
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        {LiteralsService.getLiteral('CryptoDrawer.totalSection.heading')}
                    </Typography>
                    <Typography variant={TypographyVariants.HEADING_XL} className={styles.totalAmount}>
                        {formatCryptoAmount(totalAmount)}
                    </Typography>
                    <Typography variant={TypographyVariants.BODY}>
                        {selectedAsset
                            ? LiteralsService.getLiteral('CryptoDrawer.totalSection.priceUpdate', [
                                  formatCryptoAmount(selectedAsset.price),
                                  TimeAgoService.parse(new Date(selectedAsset.updated.toString()))
                              ])
                            : ''}
                    </Typography>
                </div>

                <div className={styles.buttonsContainer}>
                    <ButtonPrimary
                        onClick={handleUpsert}
                        disabled={errors?.valueError || errors?.quantityError}
                        data-cy="cryptoSaveButton"
                    >
                        {currentCrypto
                            ? LiteralsService.getLiteral('CryptoDrawer.buttonLabelUpdate')
                            : LiteralsService.getLiteral('CryptoDrawer.buttonLabelAdd')}
                    </ButtonPrimary>
                    {currentCrypto ? (
                        <ButtonSecondary onClick={handleDelete} disabled={!currentCrypto} data-cy="cryptoDeleteButton">
                            {LiteralsService.getLiteral('CommonText.delete')}
                        </ButtonSecondary>
                    ) : (
                        <ButtonSecondary onClick={handleBack} data-cy="cryptoCancelButton">
                            {LiteralsService.getLiteral('CommonText.cancel')}
                        </ButtonSecondary>
                    )}
                </div>
            </div>
        </Drawer>
    );
}
